import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { AuditViewRow } from "@amber/contracts";
import { AppLink, Button, SegmentedFilter, Tag, formatDateTime } from "@amber/ui";
import {
  PROFILE_HISTORY_CATEGORIES,
  PROFILE_HISTORY_LABELS,
  type MergeView,
  type ProfileDetail,
  type ProfileHistoryCategory,
} from "../../contracts/index";
import { profileHistoryQuery } from "../queries";
import { ReverseMergeDialog } from "./ReverseMergeDialog";

const ID_LIKE = /^[a-z]{3}_[0-9A-Z]{26}$/;
const MERGE_DONE = new Set(["profiles.merged", "profiles.merged_away", "profiles.split"]);
const MERGE_OPENED: Partial<Record<string, MergeView["state"]>> = {
  "profiles.merge_started": "started",
  "profiles.split_started": "started",
  "profiles.merge_reverse_started": "reversing",
};
// Profiles brief › Rules: no revision or version numbers in routine views.
const HIDDEN_KEYS = new Set(["revision", "base_revision", "version"]);

function valueText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return ID_LIKE.test(value) ? "" : value;
  if (Array.isArray(value)) {
    if (value.some((v) => typeof v === "object" && v !== null)) return String(value.length);
    return value.map(valueText).filter(Boolean).join(", ");
  }
  if (typeof value === "object")
    return Object.entries(value)
      .filter(([k, v]) => !k.endsWith("_id") && valueText(v) !== "")
      .map(([k, v]) => `${k.replace(/_/g, " ")}: ${valueText(v)}`)
      .join("; ");
  return JSON.stringify(value);
}

function changes(row: AuditViewRow): { key: string; before: string; after: string }[] {
  const before = row.before ?? {};
  const after = row.after ?? {};
  return Object.keys(after)
    .filter((k) => !k.endsWith("_id") && !HIDDEN_KEYS.has(k))
    .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .map((k) => ({ key: k.replace(/_/g, " "), before: valueText(before[k]), after: valueText(after[k]) }))
    .filter((c) => c.before !== c.after);
}

function mergeOf(row: AuditViewRow, merges: readonly MergeView[]): MergeView | undefined {
  const id = row.after?.["merge_id"] ?? row.before?.["merge_id"];
  return typeof id === "string" ? merges.find((m) => m.id === id) : undefined;
}

function mergeTitle(m: MergeView): string {
  if (m.kind === "split") return m.role === "survivor" ? `Split off ${m.other.name}` : `Split from ${m.other.name}`;
  return m.role === "survivor" ? `Merged ${m.other.name} into this profile` : `Merged into ${m.other.name}`;
}

function MergeAction({
  merge,
  canEdit,
  onReverse,
}: {
  merge: MergeView;
  canEdit: boolean;
  onReverse: (merge: MergeView) => void;
}) {
  if (merge.state === "reversed") return <Tag tone="quiet">Reversed</Tag>;
  if (!merge.reversible) return null;
  return (
    <Button
      size="sm"
      disabledReason={canEdit ? null : "Only Editors and above can reverse merges"}
      onClick={() => {
        onReverse(merge);
      }}
    >
      Reverse merge…
    </Button>
  );
}

function HistoryEntry({
  row,
  merges,
  canEdit,
  onReverse,
}: {
  row: AuditViewRow;
  merges: readonly MergeView[];
  canEdit: boolean;
  onReverse: (merge: MergeView) => void;
}) {
  const merge = MERGE_DONE.has(row.action) ? mergeOf(row, merges) : undefined;
  const opened = MERGE_OPENED[row.action];
  const open = opened !== undefined && mergeOf(row, merges)?.state === opened;
  const diff = row.redacted ? [] : changes(row);
  return (
    <li className="grid grid-cols-[130px_minmax(0,1fr)_auto] items-start gap-3 border-t border-divider py-2.5 max-compact:grid-cols-[minmax(0,1fr)_auto]">
      <span className="font-mono text-md text-muted max-compact:col-span-2">{formatDateTime(row.at)}</span>
      <span className="min-w-0">
        <b className="font-strong">{merge ? mergeTitle(merge) : row.action_label}</b>
        <span className="block text-md text-muted">
          {row.actor.name}
          {row.reason && !row.redacted ? ` · Reason: “${row.reason}”` : ""}
        </span>
        {diff.length > 0 ? (
          <dl className="m-0 mt-1 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-md">
            {diff.map((c) => (
              <div key={c.key} className="contents">
                <dt className="text-muted">{c.key}</dt>
                <dd className="m-0 min-w-0 break-words">
                  {c.before ? <del className="text-muted">{c.before}</del> : <span className="text-muted">—</span>}
                  {" → "}
                  {c.after ? <ins className="no-underline">{c.after}</ins> : <span className="text-muted">—</span>}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </span>
      <span>
        {merge && row.action === "profiles.merged" ? (
          <MergeAction merge={merge} canEdit={canEdit} onReverse={onReverse} />
        ) : null}
        {open ? <Tag tone="waiting">In progress</Tag> : null}
      </span>
    </li>
  );
}

export function ProfileHistory({
  profile,
  canEdit,
  filter,
  onFilter,
}: {
  profile: ProfileDetail;
  canEdit: boolean;
  filter: ProfileHistoryCategory;
  onFilter: (filter: ProfileHistoryCategory) => void;
}) {
  const history = useInfiniteQuery(profileHistoryQuery(profile.id, filter));
  const [reversing, setReversing] = useState<MergeView | null>(null);
  const pages = history.data?.pages ?? [];
  const rows = pages.flatMap((p) => p.items);
  const counts = pages[0]?.counts;
  const remaining = (pages[0]?.total ?? 0) - rows.length;
  return (
    <div className="overflow-auto px-4 pb-6 text-ui">
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <SegmentedFilter<ProfileHistoryCategory>
          label="Filter history"
          value={filter}
          options={PROFILE_HISTORY_CATEGORIES.map((c) => ({
            value: c,
            label: PROFILE_HISTORY_LABELS[c],
            ...(counts ? { count: counts[c] } : {}),
          }))}
          onChange={onFilter}
        />
        <span className="flex-1 text-md text-muted">Who, when, why, before and after · newest first</span>
        <AppLink href={`/settings/audit?subject=${profile.id}`} className="text-md">
          Open in the audit log
        </AppLink>
      </div>
      {history.isPending ? <p className="m-0 mt-3 text-md text-muted">Loading history…</p> : null}
      {history.data && rows.length === 0 ? (
        <p className="m-0 mt-3 text-md text-muted">Nothing in this category yet.</p>
      ) : null}
      <ol className="m-0 mt-3 list-none p-0" aria-label="History entries">
        {rows.map((r) => (
          <HistoryEntry key={r.id} row={r} merges={profile.merges} canEdit={canEdit} onReverse={setReversing} />
        ))}
      </ol>
      {history.hasNextPage ? (
        <div className="flex justify-center border-t border-divider pt-2">
          <Button
            size="sm"
            aria-busy={history.isFetchingNextPage || undefined}
            onClick={() => {
              void history.fetchNextPage();
            }}
          >
            Show more{remaining > 0 ? ` (${String(remaining)} older)` : ""}
          </Button>
        </div>
      ) : null}
      {reversing ? (
        <ReverseMergeDialog
          merge={reversing}
          onClose={() => {
            setReversing(null);
          }}
        />
      ) : null}
    </div>
  );
}
